<script setup>
import { ref, computed, watch } from 'vue'
import { Inertia } from '@inertiajs/inertia'
import { useForm, usePage } from '@inertiajs/inertia-vue3'
import ActionSection from '@/Components/ActionSection.vue'
import ConfirmsPassword from '@/Components/ConfirmsPassword.vue'
import TextInput from '@/Shared/TextInput'
import LoadingButton from '@/Shared/LoadingButton'

const props = defineProps({
  requiresConfirmation: Boolean,
})

const enabling = ref(false)
const confirming = ref(false)
const disabling = ref(false)
const qrCode = ref(null)
const setupKey = ref(null)
const recoveryCodes = ref([])

const confirmationForm = useForm({
  code: '',
})

const twoFactorEnabled = computed(
  () => ! enabling.value && usePage().props.value.user?.two_factor_enabled,
)

const isVolunteer = computed(
  () => usePage().props.value.auth.user?.isVolunteer,
)
const applicationForceTwoFactor = computed(
  () => usePage().props.value.auth.user?.application_force_two_factor,
)

watch(twoFactorEnabled, () => {
  if (! twoFactorEnabled.value) {
    confirmationForm.reset()
    confirmationForm.clearErrors()
  }
})

const enableTwoFactorAuthentication = () => {
  enabling.value = true
  Inertia.post('/user/two-factor-authentication', {}, {
    preserveScroll: true,
    onSuccess: () => Promise.all([
      showQrCode(),
      showSetupKey(),
      showRecoveryCodes(),
    ]),
    onFinish: () => {
      enabling.value = false
      confirming.value = props.requiresConfirmation
    },
  })
}

const showQrCode = () => {
  return axios.get('/user/two-factor-qr-code').then(response => {
    qrCode.value = response.data.svg
  })
}

const showSetupKey = () => {
  return axios.get('/user/two-factor-secret-key').then(response => {
    setupKey.value = response.data.secretKey
  })
}

const showRecoveryCodes = () => {
  return axios.get('/user/two-factor-recovery-codes').then(response => {
    recoveryCodes.value = response.data
  })
}

const confirmTwoFactorAuthentication = () => {
  confirmationForm.post('/user/confirmed-two-factor-authentication', {
    errorBag: 'confirmTwoFactorAuthentication',
    preserveScroll: true,
    preserveState: true,
    onSuccess: () => {
      confirming.value = false
      qrCode.value = null
      setupKey.value = null
    },
  })
}

const regenerateRecoveryCodes = () => {
  axios
    .post('/user/two-factor-recovery-codes')
    .then(() => showRecoveryCodes())
}

const disableTwoFactorAuthentication = () => {
  disabling.value = true

  Inertia.delete('/user/two-factor-authentication', {
    preserveScroll: true,
    onSuccess: () => {
      disabling.value = false
      confirming.value = false
    },
  })
}
</script>

<template>
  <ActionSection>
    <template #title>
      {{ $t('profile.two-factor.title') }}
    </template>

    <template #description>
      {{ $t('profile.two-factor.description') }}
    </template>

    <template #content>
      <h3 v-if="twoFactorEnabled && ! confirming" class="text-lg font-medium text-gray-900">
        {{ $t('profile.two-factor.enabled') }}
      </h3>

      <h3 v-else-if="twoFactorEnabled && confirming" class="text-lg font-medium text-gray-900">
        {{ $t('profile.two-factor.enabled-finish') }}
      </h3>

      <h3 v-else class="text-lg font-medium text-gray-900">
        {{ $t('profile.two-factor.not-enabled') }}
      </h3>

      <div class="mt-3 max-w-xl text-sm text-gray-600">
        <p>
          {{ $t('profile.two-factor.enable-description') }}
        </p>
      </div>

      <div v-if="twoFactorEnabled">
        <div v-if="qrCode">
          <div class="mt-4 max-w-xl text-sm text-gray-600">
            <p v-if="confirming" class="font-semibold">
              {{ $t('profile.two-factor.to-finish') }}
            </p>

            <p v-else>
              {{ $t('profile.two-factor.is-enabled') }}
            </p>
          </div>

          <div class="mt-4" v-html="qrCode" />

          <div v-if="setupKey" class="mt-4 max-w-xl text-sm text-gray-600">
            <p class="font-semibold">
              Setup Key: <span v-html="setupKey" />
            </p>
          </div>

          <div v-if="confirming" class="mt-4">
            <TextInput
              id="code"
              v-model="confirmationForm.code"
              :error="confirmationForm.errors.code"
              type="text"
              name="code"
              class="block mt-1 w-1/2"
              inputmode="numeric"
              autofocus
              autocomplete="one-time-code"
              @keyup.enter="confirmTwoFactorAuthentication"
            />
          </div>
        </div>

        <div v-if="recoveryCodes.length > 0 && ! confirming">
          <div class="mt-4 max-w-xl text-sm text-gray-600">
            <p class="font-semibold">
              {{ $t('profile.two-factor.recovery-codes') }}
            </p>
          </div>

          <div class="grid gap-1 max-w-xl mt-4 px-4 py-4 font-mono text-sm bg-gray-100 rounded-lg">
            <div v-for="code in recoveryCodes" :key="code">
              {{ code }}
            </div>
          </div>
        </div>
      </div>

      <div class="mt-5">
        <div v-if="! twoFactorEnabled">
          <ConfirmsPassword @confirmed="enableTwoFactorAuthentication">
            <loading-button type="button" class="btn-indigo ml-auto" :loading="enabling">
              Enable
            </loading-button>
          </ConfirmsPassword>
        </div>

        <div v-else>
          <ConfirmsPassword @confirmed="confirmTwoFactorAuthentication">
            <loading-button
              v-if="confirming"
              type="button"
              class="btn-indigo mr-3"
              :loading="enabling"
            >
              Confirm
            </loading-button>
          </ConfirmsPassword>

          <ConfirmsPassword @confirmed="regenerateRecoveryCodes">
            <button v-if="recoveryCodes.length > 0 && ! confirming" class="text-red-600 hover:underline" tabindex="-1" type="button">
              Regenerate Recovery Codes
            </button>
          </ConfirmsPassword>

          <ConfirmsPassword @confirmed="showRecoveryCodes">
            <button v-if="recoveryCodes.length === 0 && ! confirming" class="text-red-600 hover:underline" tabindex="-1" type="button">
              Show Recovery Codes
            </button>
          </ConfirmsPassword>

          <ConfirmsPassword @confirmed="disableTwoFactorAuthentication">
            <button v-if="confirming" class="text-red-600 hover:underline inline-block" tabindex="-1" type="button">
              Cancel
            </button>
          </ConfirmsPassword>

          <span v-if="(isVolunteer && !applicationForceTwoFactor) || !isVolunteer">
            <ConfirmsPassword @confirmed="disableTwoFactorAuthentication">
              <button v-if="! confirming" class="btn-red hover:underline ml-2" tabindex="-1" type="button">
                Disable
              </button>
            </ConfirmsPassword>
          </span>
        </div>
      </div>
    </template>
  </ActionSection>
</template>
